import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

import useAuth from '../../hooks/useAuth';
import useParentAuth from '../../hooks/useParentAuth';
import { getLandingRouteForUser } from '../../utils/auth';
import PrimaryButton from './buttons/PrimaryButton';

function NavLinks({ navLinks, mobile, toggleMenu, isAuthenticated, handleLogout }) {
  return (
    <>
      {navLinks.map((link) => (
        <a
          key={link.href}
          href={link.href}
          onClick={mobile ? toggleMenu : undefined}
          className={
            mobile
              ? 'block px-4 py-3 text-light hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-300 font-medium'
              : 'text-light hover:text-accent transition-colors duration-300 font-medium'
          }
        >
          {link.label}
        </a>
      ))}

      {isAuthenticated && <PrimaryButton onClick={handleLogout}>Logout</PrimaryButton>}
    </>
  );
}

function ParentLink({ to, label, icon: Icon, active, mobile, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={[
        'flex items-center gap-2 font-medium transition-colors duration-300',
        mobile
          ? 'rounded-lg px-4 py-3 text-light hover:bg-white/5 hover:text-white'
          : 'pb-2',
        active
          ? 'text-[#F6911D]'
          : 'text-white/80 hover:text-white',
        !mobile && active ? 'border-b-2 border-[#F6911D]' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon sx={{ fontSize: mobile ? 18 : 20 }} />
      <span>{label}</span>
    </Link>
  );
}

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const teacherAuth = useAuth();
  const parentAuth = useParentAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isTeacherArea = location.pathname.startsWith('/admin') || location.pathname.startsWith('/teacher');
  const activeAuth = isTeacherArea ? teacherAuth : parentAuth.isAuthenticated ? parentAuth : teacherAuth;
  const { logout, isAuthenticated, user, fullName, roleLabel, isParent, isStudent, isTeacher, isAdmin } = activeAuth;

  const isParentArea = isParent || isStudent;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleLogout = async () => {
    await logout();

    navigate('/');
  };

  const navLinks = isAdmin
    ? [
        { label: 'Dashboard', href: '/admin/dashboard' },
        { label: 'Teachers', href: '/admin/teachers' },
        { label: 'Pending Approvals', href: '/admin/pending-approvals' },
      ]
    : isTeacher
      ? [
          { label: 'Home', href: '/' },
          { label: 'About', href: '/about' },
          { label: 'My Profile', href: `/teacher/${user.slug}` },
        ]
    : [
        { label: 'Home', href: '/' },
        { label: 'About', href: '/about' },
      ];
  const isParentHomeActive = location.pathname === '/parent';
  const isParentStudentsActive = location.pathname.startsWith('/parent/students');
  const isParentClassesActive = location.pathname.startsWith('/parent/student-classes');

  if (isParentArea) {
    return (
      <>
        <nav className="relative z-50 border-b border-white/10 bg-[#111111]/95 backdrop-blur">
          <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link className="shrink-0" to={getLandingRouteForUser(user)}>
              <img className="w-[80px]" src="/assets/images/logo.png" alt="Logo" />
            </Link>

            <div className="hidden items-center gap-6 md:flex lg:gap-8">
              <div className="flex items-center gap-2 text-white/90">
                <span className="text-sm font-medium">{fullName}</span>
                {roleLabel ? (
                  <span className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] uppercase tracking-wide text-white/55">
                    {roleLabel}
                  </span>
                ) : null}
                <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
              </div>
              <span className="h-8 w-px bg-white/10" />
              <div className="flex items-center gap-8 text-sm">
                {isParent ? <ParentLink to="/parent" label="Home" icon={HomeOutlinedIcon} active={isParentHomeActive} /> : null}
                {isParent ? (
                  <ParentLink to="/parent/students" label="Students" icon={InfoOutlinedIcon} active={isParentStudentsActive} />
                ) : null}
                {isStudent ? (
                  <ParentLink
                    to="/parent/student-classes"
                    label="My Classes"
                    icon={HomeOutlinedIcon}
                    active={isParentClassesActive}
                  />
                ) : null}
                <button type="button" onClick={handleLogout} className="flex items-center gap-2 text-white/80 transition-colors duration-300 hover:text-white">
                  <LogoutOutlinedIcon sx={{ fontSize: 20 }} />
                  <span>Logout</span>
                </button>
              </div>
            </div>

            <div className="md:hidden">
              <button
                type="button"
                onClick={toggleMenu}
                className="rounded-md px-3 py-2 text-white/90 hover:bg-white/5"
              >
                ☰
              </button>
            </div>
          </div>
        </nav>

        <div
          className={[
            'fixed inset-x-0 top-20 z-40 border-b border-white/10 bg-[#111111]/98 px-4 py-3 backdrop-blur md:hidden',
            isMenuOpen ? 'translate-y-0' : '-translate-y-[140%]',
          ].join(' ')}
        >
          <div className="flex flex-col gap-2">
            <div className="px-4 py-2 text-sm font-medium text-white/80">{fullName}</div>
            {roleLabel ? <div className="px-4 text-xs uppercase tracking-wider text-white/45">{roleLabel}</div> : null}
            {isParent ? (
              <ParentLink
                to="/parent"
                label="Home"
                icon={HomeOutlinedIcon}
                active={isParentHomeActive}
                mobile
                onClick={toggleMenu}
              />
            ) : null}
            {isParent ? (
              <ParentLink
                to="/parent/students"
                label="Students"
                icon={InfoOutlinedIcon}
                active={isParentStudentsActive}
                mobile
                onClick={toggleMenu}
              />
            ) : null}
            {isStudent ? (
              <ParentLink
                to="/parent/student-classes"
                label="My Classes"
                icon={HomeOutlinedIcon}
                active={isParentClassesActive}
                mobile
                onClick={toggleMenu}
              />
            ) : null}
            <button type="button" onClick={handleLogout} className="flex items-center gap-2 rounded-lg px-4 py-3 text-light hover:bg-white/5 hover:text-white">
              <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <nav className="relative z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href={isAdmin ? '/admin/dashboard' : '/'}>
                <img className="w-[80px]" src="/assets/images/logo.png" alt="Logo" />
              </a>
            </div>

            {/* Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <NavLinks
                navLinks={navLinks}
                mobile={false}
                isAuthenticated={isAuthenticated}
                handleLogout={handleLogout}
              />
            </div>

            {/* Mobile button */}
            <div className="md:hidden">
              <button onClick={toggleMenu}>☰</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`fixed right-0 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <NavLinks
          navLinks={navLinks}
          mobile={true}
          toggleMenu={toggleMenu}
          isAuthenticated={isAuthenticated}
          handleLogout={handleLogout}
        />
      </div>
    </>
  );
}

export default Navbar;
